export type AiTextContent = {
  type: 'text'
  text: string
  /**
   * Anthropic prompt-caching marker. When set, the proxy emits a
   * `cache_control: { type: 'ephemeral' }` annotation on the corresponding
   * Anthropic block. Ignored by non-Anthropic providers.
   *
   * You usually don't need to set this. By default, the proxy auto-caches
   * the system prefix for Anthropic calls — see `AiChatCompletionRequest`.
   * Set this explicitly when you want to control which segments are cached
   * (e.g. caching mid-conversation history alongside the system, or
   * disabling the proxy's default by claiming the breakpoint yourself on
   * a different block).
   */
  cacheControl?: { type: 'ephemeral' }
}

/** Image referenced by URL. OpenAI/DeepSeek accept directly; the proxy fetches and base64-encodes for Anthropic. */
export type AiImageUrlContent = {
  type: 'image_url'
  image_url: {
    /** http(s):// URL or data:image/<type>;base64,<data> URI. */
    url: string
    /** Optional hint for vision models that support it. */
    detail?: 'low' | 'high' | 'auto'
  }
}

/** Image as raw base64 data. */
export type AiImageContent = {
  type: 'image'
  image: {
    format: 'jpeg' | 'png' | 'gif' | 'webp'
    /** Base64-encoded image bytes (no `data:` prefix). */
    data: string
  }
}

/**
 * Assistant-emitted tool invocation, used in multi-turn tool conversations
 * when echoing prior assistant turns back to the model. The same data is
 * surfaced on a fresh response via the `toolCalls` field on the assistant
 * message; this block type exists so callers can replay prior turns.
 */
export type AiToolUseContent = {
  type: 'tool_use'
  id: string
  name: string
  input: Record<string, unknown>
}

/**
 * User-supplied result for a previously emitted tool call. Used when feeding
 * tool execution output back to the model on the next turn.
 */
export type AiToolResultContent = {
  type: 'tool_result'
  toolUseId: string
  content: string | AiContentBlock[]
  isError?: boolean
}

export type AiContentBlock =
  | AiTextContent
  | AiImageUrlContent
  | AiImageContent
  | AiToolUseContent
  | AiToolResultContent

export interface Tool {
  name: string
  description: string
  /** JSON Schema describing the tool's input arguments. */
  inputSchema: object
}

export type ToolChoice =
  | 'auto'
  | 'any'
  | 'none'
  | { type: 'tool'; name: string }

export interface ToolUse {
  id: string
  name: string
  input: Record<string, unknown>
}

/**
 * Constrains the model's output format.
 *
 * - `text`: free-form text (default).
 * - `json_object`: model is asked to emit valid JSON. No schema enforcement.
 * - `json_schema`: model is asked to emit JSON matching the given schema.
 *   The proxy enforces the schema on every provider:
 *     - OpenAI / DeepSeek: native Structured-Outputs `response_format`.
 *     - Anthropic: schema is appended to the system prompt; the response is
 *       parsed and validated server-side. On validation failure the proxy
 *       retries once with the validator errors fed back to the model. If
 *       the retry also fails, the request fails with HTTP 422.
 *     - Gemini: native `responseSchema`.
 *
 *   `strict` (default `false`) maps to OpenAI's `strict` Structured-Outputs
 *   flag and is otherwise informational.
 *
 *   For back-compat, the OpenAI native nested shape
 *   (`json_schema: { name, schema, strict? }`) is also accepted and passed
 *   through unchanged on the OpenAI path.
 *
 *   ### Interaction with `tools`
 *
 *   When `tools` is also set on the request, the schema applies ONLY to the
 *   model's text content. If the model invokes a tool, the tool call IS the
 *   response and the schema is not enforced (matches OpenAI's native
 *   behavior). To force schema-conforming text even when tools are
 *   available, omit `tools` from the request.
 */
export type AiResponseFormat =
  | { type: 'text' }
  | { type: 'json_object' }
  | {
      type: 'json_schema'
      schema?: object
      strict?: boolean
      json_schema?: {
        name?: string
        description?: string
        schema?: object
        strict?: boolean | null
      }
    }

export interface AiMessage {
  /**
   * Depends on the model you are using. Most common is user, system, and assistant.
   * If you are unsure, look up the specific model you are using.
   */
  role: string
  /** Either a plain string or an array of content blocks (text + image) for multimodal messages. */
  content: string | AiContentBlock[]
}

/**
 * Stable text generation model classes. Each class maps to a server-owned
 * default model, so a class-only request keeps working when individual models
 * are retired or rotated. Exact model ids (`model`) are the advanced,
 * best-effort alternative.
 *
 * Canonical source: `ModelClass` in `server/cloud-run/src/config/creatorEntitlement.ts`
 * (mirrored here — the published SDK cannot import server code).
 */
export type TextGenModelClass = 'quick' | 'standard' | 'power'

export type AiChatCompletionRequest = {
  /** Advanced, best-effort exact model request (e.g., 'gpt-5.6-luna', 'claude-sonnet-4-6'). */
  model?: string
  /** Stable fallback. Defaults to quick when absent. */
  modelClass?: TextGenModelClass
  /** Array of message objects in OpenAI chat format */
  messages: Array<AiMessage>
  /**
   * Structured system prompt. Mirrors Anthropic's `system` field shape so the
   * caller can mark specific segments of a large static prefix as cacheable
   * via `cacheControl`.
   *
   * - `string`: passed verbatim as the system instruction.
   * - `Array<AiTextContent>`: each block becomes a system text block.
   *
   * ### Anthropic prompt caching (default behavior)
   *
   * For Anthropic models, the proxy automatically caches the system prefix:
   * if neither `system` nor any block in `messages` carries an explicit
   * `cacheControl` marker, the proxy attaches `cache_control: 'ephemeral'`
   * to the LAST text block of the system. This is the cheap default —
   * Anthropic's pricing makes it a net win within ~2 cache hits on the
   * same prefix within the 5-minute TTL, and sub-threshold systems
   * (<1024 tokens Sonnet/Opus, <2048 Haiku) are ignored by Anthropic so
   * the marker is a no-op for small prompts.
   *
   * To **override** the default — for example, to cache a specific segment
   * of the system or to additionally cache a slice of the user-message
   * history — set `cacheControl: { type: 'ephemeral' }` on the block(s) you
   * want as cache breakpoints. The presence of any explicit marker disables
   * the proxy's auto-cache so your strategy isn't interfered with.
   *
   * For non-Anthropic providers, the array is flattened to a single string
   * and `cacheControl` is silently dropped. Coexists with `role: 'system'`
   * entries in `messages`; both are concatenated.
   */
  system?: string | Array<AiTextContent>
  /** Maximum number of tokens to generate */
  maxTokens?: number
  /** Upper bound for tokens generated, including visible output and reasoning tokens */
  maxCompletionTokens?: number
  /**
   * Requested reasoning (thinking) effort for reasoning-capable models.
   *
   * Per-provider support:
   * - **OpenAI** (`gpt-5` family): all levels, passed through natively as
   *   `reasoning_effort`.
   * - **Gemini**: all levels, translated to the model generation's thinking
   *   config (thinking level on Gemini 3.x, thinking budget on Gemini 2.5).
   * - **Anthropic** (`claude-*`) and **DeepSeek**: not yet supported — a
   *   request carrying this field is rejected with HTTP 400.
   *
   * `'none'` requests minimal/disabled reasoning where the model allows it;
   * models that cannot fully disable thinking (e.g. `gemini-2.5-pro`,
   * Gemini 3.x) clamp to their minimum instead.
   *
   * Reasoning tokens are billed as OUTPUT tokens, so higher efforts increase
   * completion-token usage and cost. When the model reasons, the response's
   * `usage.reasoning_tokens` reports how many of the completion tokens were
   * spent on reasoning.
   *
   * Older backends that predate this field silently ignore it.
   */
  reasoningEffort?: 'none' | 'low' | 'medium' | 'high'
  /** Sampling temperature (0.0 to 2.0) */
  temperature?: number
  /** Nucleus sampling parameter (0.0 to 1.0) */
  topP?: number
  /** Top-k sampling parameter */
  topK?: number
  /** Number of completions to generate (1-10) */
  n?: number
  /** Up to 4 sequences where the API will stop generating tokens */
  stop?: string | string[]
  /** Presence penalty (-2.0 to 2.0) */
  presencePenalty?: number
  /** Frequency penalty (-2.0 to 2.0) */
  frequencyPenalty?: number
  /** Token logit biases */
  logitBias?: Record<string, number>
  /** Object specifying the format that the model must output. See {@link AiResponseFormat}. */
  responseFormat?: AiResponseFormat
  /** Random seed for reproducible outputs (Beta feature) */
  seed?: number
  /** Unique identifier representing your end-user */
  user?: string
  /** Timeout in seconds for completion requests */
  timeout?: number
  /** Whether to return log probabilities of output tokens */
  logprobs?: boolean
  /** Number of most likely tokens (0-5) to return at each position */
  topLogprobs?: number
  /** Unique identifier for tracking and managing safety-related requests */
  safetyIdentifier?: string
  /** Dictionary of headers to be sent with the request */
  headers?: Record<string, any>
  /** Alternative to headers, used to send extra headers in LLM API request */
  extraHeaders?: Record<string, any>
  

  /** Custom tags for organizing completions */
  tags?: string[]

  tools?: Tool[]
  toolChoice?: ToolChoice

  /**
   * @deprecated No longer used. Local dev uses HttpAiApi with Firebase auth. Will be removed in a future major version.
   */
  apiKey?: string
}

export type AiChatCompletionData = {
  id: string
  ullm_id?: string
  cost?: {
    prompt_cost: number
    completion_cost: number
  }
  object: unknown
  created: number
  model: string
  choices: Array<{
    index: number
    message: {
      role: string
      content: string
      /** Present only for reasoning models (e.g. deepseek-r1): the model's thinking, separate from the visible `content`. */
      reasoning_content?: string
      /** Present only when the model invoked one or more tools on this turn. */
      toolCalls?: ToolUse[]
    }
    finish_reason: string
  }>
  usage: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    reasoning_tokens?: number
    cache_read_tokens?: number
    cache_write_tokens?: number
  }
}

/**
 * A single event from a streaming chat completion. Mirrors the wire shape
 * the proxy emits on `/v1/llm/completion-stream`.
 *
 * - `delta`: incremental assistant text. Concatenate `text` across all
 *   `delta` events to reconstruct the final message.
 * - `reasoning`: incremental reasoning / chain-of-thought text, emitted only
 *   by reasoning models (e.g. deepseek-r1). Concatenate `text` across all
 *   `reasoning` events (like `delta`) to reconstruct the full reasoning trace.
 *   Streamed on a distinct channel so consumers can render or ignore it
 *   independently of the answer; content-only consumers can skip it.
 * - `tool_call_chunk`: partial tool-call metadata. Providers stream tool
 *   arguments as multiple chunks; buffer by `index` until `done`.
 * - `done`: terminal event for a successful stream. Carries `finishReason`
 *   and aggregate token totals (same shape as the non-streaming response's
 *   `usage`). Always the last event yielded.
 *
 * Errors are out-of-band — consumers see them as the iterable throwing.
 */
export type AiChatCompletionStreamChunk =
  | { type: 'delta'; text: string }
  | { type: 'reasoning'; text: string }
  | {
      type: 'tool_call_chunk'
      index: number
      id?: string
      name?: string
      argumentsDelta?: string
    }
  | {
      type: 'done'
      finishReason: string
      usage: AiChatCompletionData['usage']
      /**
       * Friendly id of the model that actually ran (after class fallback).
       * Optional so a new SDK stays valid against an older backend that does
       * not emit it.
       */
      model?: string
    }

/**
 * Request for a templated ("prompt") completion. Games whose textGen config is
 * `mode: 'templated'` don't send raw `model`/`system`/`messages` — they invoke
 * a server-authored prompt by id. The server owns the model, the system
 * prompt, and any `{{var}}` slots; the game contributes only the player's
 * turn via `input`.
 */
export interface PromptCompletionRequest {
  /** Id of a server-authored prompt declared in rundot/textGen.config.json. */
  promptId: string
  /** The player's turn; isolation-wrapped server-side. Text-only in v1. */
  input?: string
}

/**
 * Builds the wire object for a templated prompt call explicitly (never
 * spreads the caller's object) so only the templated-call fields ride the
 * wire. Shared by every AiApi transport.
 */
export function toPromptWireRequest(request: PromptCompletionRequest): PromptCompletionRequest {
  return { promptId: request.promptId, input: request.input }
}

export interface AiChatCompletionStreamOptions {
  /**
   * Caller-supplied AbortSignal. Aborting causes the iterable to stop
   * yielding and the host to close the upstream SSE socket. Equivalent to
   * `break`ing out of `for await`; provided as a separate handle for callers
   * that already manage an AbortController for the surrounding operation.
   */
  signal?: AbortSignal
}

export interface AiApi {
  requestChatCompletionAsync(
    request: AiChatCompletionRequest,
  ): Promise<AiChatCompletionData>
  getAvailableCompletionModels(): Promise<Array<string>>

  /**
   * Streaming sibling of `requestChatCompletionAsync`. Returns an async
   * iterable of chunks. Cancel by `break`ing out of `for await`, calling
   * `return()` on the iterator, or aborting the optional `signal`.
   *
   * @example
   * Text-only consumption — concatenate `delta.text` and read usage on `done`:
   * ```ts
   * const stream = textGen.requestChatCompletionStreamAsync({ ... })
   * let text = ''
   * for await (const chunk of stream) {
   *   if (chunk.type === 'delta') text += chunk.text
   *   if (chunk.type === 'done') console.log('usage', chunk.usage)
   * }
   * ```
   *
   * @example
   * Buffering `tool_call_chunk` events into `ToolUse[]`. Providers stream
   * tool arguments as a sequence of `argumentsDelta` fragments keyed by
   * `index`; concatenate them and JSON-parse once `done` arrives. A
   * `tool_call_chunk` chunk carries the tool's `id` and `name` once they
   * become known (typically on the first chunk for that index), with
   * argument-only chunks following:
   * ```ts
   * import type {
   *   AiChatCompletionStreamChunk,
   *   ToolUse,
   * } from '@series-inc/rundot-game-sdk'
   *
   * interface PartialToolCall { id?: string; name?: string; args: string }
   *
   * const buffer = new Map<number, PartialToolCall>()
   * const toolCalls: ToolUse[] = []
   * let text = ''
   *
   * for await (const chunk of stream) {
   *   switch (chunk.type) {
   *     case 'delta':
   *       text += chunk.text
   *       break
   *     case 'tool_call_chunk': {
   *       const entry = buffer.get(chunk.index) ?? { args: '' }
   *       if (chunk.id !== undefined) entry.id = chunk.id
   *       if (chunk.name !== undefined) entry.name = chunk.name
   *       if (chunk.argumentsDelta !== undefined) {
   *         entry.args += chunk.argumentsDelta
   *       }
   *       buffer.set(chunk.index, entry)
   *       break
   *     }
   *     case 'done':
   *       for (const entry of buffer.values()) {
   *         if (entry.id === undefined || entry.name === undefined) continue
   *         toolCalls.push({
   *           id: entry.id,
   *           name: entry.name,
   *           input: entry.args === '' ? {} : JSON.parse(entry.args),
   *         })
   *       }
   *       break
   *   }
   * }
   * ```
   *
   * Gemini note: text streaming is supported; tool-use on Gemini streaming
   * is deferred — the iterable throws `AppError(400)` when `request.tools`
   * is non-empty.
   */
  requestChatCompletionStreamAsync(
    request: AiChatCompletionRequest,
    options?: AiChatCompletionStreamOptions,
  ): AsyncIterable<AiChatCompletionStreamChunk>

  /**
   * Invoke a server-authored prompt by id (templated textGen mode).
   *
   * Unlike `requestChatCompletionAsync`, the game does not choose the model
   * or author the messages — those live server-side in the prompt declared in
   * `rundot/textGen.config.json`. The optional `input` carries the player's
   * turn and is isolation-wrapped server-side; it is text-only in v1.
   *
   * There are no client-supplied variables in v1: `{{var}}` slots in the
   * server-side prompt resolve entirely from a trusted server-side bag, so
   * the call carries nothing to reconcile or sanitize beyond `input`.
   *
   * Note: against a templated game, the open-mode
   * `requestChatCompletionAsync` is rejected with HTTP 403 and error code
   * `AI_POLICY_DENIED` — use this method instead.
   *
   * @example
   * ```ts
   * const response = await textGen.requestPromptCompletionAsync({
   *   promptId: 'npc-banter',
   *   input: 'What do you think of the swamp zone?',
   * })
   * console.log(response.choices[0].message.content)
   * ```
   */
  requestPromptCompletionAsync(
    request: PromptCompletionRequest,
  ): Promise<AiChatCompletionData>

  /**
   * Streaming sibling of `requestPromptCompletionAsync`. Returns an async
   * iterable of chunks — the same chunk contract as
   * `requestChatCompletionStreamAsync` (see its docs for chunk buffering and
   * cancellation semantics). Cancel by `break`ing out of `for await`, calling
   * `return()` on the iterator, or aborting the optional `signal`.
   *
   * @example
   * ```ts
   * const stream = textGen.requestPromptCompletionStreamAsync({
   *   promptId: 'npc-banter',
   *   input: 'Tell me a story.',
   * })
   * let text = ''
   * for await (const chunk of stream) {
   *   if (chunk.type === 'delta') text += chunk.text
   *   if (chunk.type === 'done') console.log('usage', chunk.usage)
   * }
   * ```
   */
  requestPromptCompletionStreamAsync(
    request: PromptCompletionRequest,
    options?: AiChatCompletionStreamOptions,
  ): AsyncIterable<AiChatCompletionStreamChunk>
}
